<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <TabDescription :text="$t('vp_stay_subtext')" :note="$t('vp_data_info')" />
  <BaseCard no-padding class="tw:p-5">
    <MetricViewHeader
      :selectedTimeFrame="selectedTimeFrame"
      @onSelectTimeFrame="selectTimeFrame"
      :title="$t('vp_trends_header')"
      :titleTooltipLabel="$t('vp_trends_tooltip')"
    />

    <Notification v-if="isWaitingMode" :iconType="'info'">{{ $t('vp_graph_empty_state') }}</Notification>
    <Notification v-else :iconType="'bulb'"><span v-html="$t(tip)" /></Notification>
    <DurationGraphContainer class="tw:mt-9" :timeFrame="selectedTimeFrame" />
  </BaseCard>

  <div class="tw:mt-12" v-if="showHotelOfferBanner" data-testid="stay-duration-hotel-offers-banner">
    <HotelOffersBanner title="hotel_offers_banner_los_header" text="hotel_offers_banner_los_subtext" />
  </div>

  <BaseCard class="tw:mt-12">
    <div class="tw:mb-9 tw:flex tw:items-center tw:justify-between">
      <MetricViewHeaderTitle :tooltipLabel="$t('vp_competitor_table_tooltip')">{{
        $t('vp_competitor_table')
      }}</MetricViewHeaderTitle>
      <div class="tw:flex tw:gap-6" v-if="competitors?.length > 0">
        <div class="tw:flex tw:items-center">
          <span class="tw:bg-blue-100 tw:border tw:border-gray-200 tw:h-[27px] tw:w-[15px] tw:mr-2" />
          <p>{{ $t('vp_property_data') }}</p>
        </div>
        <div class="tw:flex tw:items-center">
          <span class="tw:bg-blue-400 tw:border tw:border-gray-200 tw:h-[27px] tw:w-[15px] tw:mr-2" />
          <p>{{ $t('vp_percentage_indicator') }}</p>
        </div>
      </div>
    </div>
    <Notification v-if="!isCompetitorsFetching && competitors?.length > 0" iconType="info">
      <i18n-t keypath="vp_competitor_table_info" tag="span">
        <template #RateInsights>
          <router-link data-testid="rate-insights-link-length-of-stay" to="/rate-insights/view">{{
            $t('rate_insights')
          }}</router-link>
        </template>
      </i18n-t>
    </Notification>

    <DurationTable class="tw:mt-9" :timeFrame="selectedTimeFrame" />
  </BaseCard>
</template>

<script setup>
import { ref, computed } from 'vue'

import MetricViewHeader from '@/components/visitorsProfile/common/MetricViewHeader.vue'
import TabDescription from '@/components/visitorsProfile/common/TabDescription.vue'
import Notification from '@/components/visitorsProfile/common/Notification.vue'
import BaseCard from '@/components/dashboard/BaseCard.vue'
import DurationGraphContainer from '@/components/visitorsProfile/durationView/DurationGraphContainer.vue'
import MetricViewHeaderTitle from '@/components/visitorsProfile/common/MetricViewHeaderTitle.vue'
import DurationTable from '@/components/visitorsProfile/durationView/DurationTable.vue'
import HotelOffersBanner from '@/components/visitorsProfile/common/HotelOffersBanner.vue'

import { selectedAccommodationId } from '@/layouts/queries'
import { useCompetitors } from '@/components/rateInsights/queries'
import { useVisitorsProfileDuration } from '@/components/visitorsProfile/queries'

import appAnalytics from '@/utils/tracking'
import { useActiveOffer } from '@/components/hotelOffers/queries'

const selectedTimeFrame = ref('30')

const selectTimeFrame = timeFrame => {
  if (selectedTimeFrame.value !== timeFrame) {
    selectedTimeFrame.value = timeFrame
    appAnalytics.track(appAnalytics.events.VP_TIMEFRAME_CHANGED, {
      tabName: 'Length of Stay',
      timeFrame: timeFrame,
      accommodationId: selectedAccommodationId.value,
    })
  }
}

const { isVisitorsProfileDurationLoading, visitorsProfileDuration } = useVisitorsProfileDuration(selectedTimeFrame)

const { isCompetitorsFetching, competitors } = useCompetitors(selectedAccommodationId)

const dataForCurrentAccommodation = computed(() => visitorsProfileDuration.value?.[selectedAccommodationId.value])
const { hasActiveOffer } = useActiveOffer(selectedAccommodationId)
const mostPopularSearch = computed(() => {
  if (isVisitorsProfileDurationLoading.value || !dataForCurrentAccommodation.value) return ''

  const highest = Object.keys(dataForCurrentAccommodation.value).reduce((a, b) =>
    dataForCurrentAccommodation.value[a] > dataForCurrentAccommodation.value[b] ? a : b
  )
  return highest
})

const isWaitingMode = computed(() => {
  return visitorsProfileDuration.value && !dataForCurrentAccommodation.value
})

const tip = computed(() => {
  if (mostPopularSearch?.value === 'lte2days') {
    return 'vp_stay_1night_tip'
  } else if (mostPopularSearch?.value === 'days34') {
    return 'vp_stay_2nights_tip'
  } else if (mostPopularSearch?.value === 'days56') {
    return 'vp_stay_4nights_tip'
  } else if (mostPopularSearch?.value === 'days78') {
    return 'vp_stay_6nights_tip'
  } else if (mostPopularSearch?.value === 'gte9days') {
    return 'vp_stay_8nights_tip'
  } else return ''
})
const showHotelOfferBanner = computed(() => !hasActiveOffer.value && dataForCurrentAccommodation.value)
</script>
